import type { FetchOptions } from 'ofetch'

type HeaderRecord = Record<string, string>

const normalizeHeaders = (options: FetchOptions) => {
  if (!options.headers)
    options.headers = {}

  if (options.headers instanceof Headers) {
    const plainHeaders: HeaderRecord = {}

    options.headers.forEach((value, key) => {
      plainHeaders[key] = value
    })

    options.headers = plainHeaders
  }
  else if (Array.isArray(options.headers)) {
    options.headers = Object.fromEntries(options.headers)
  }

  return options.headers as HeaderRecord
}

/**
 * Base fetch instance with auth headers injected.
 * Used internally — prefer `$api` which also handles 401 retry.
 */
const _baseFetch = $fetch.create({
  retry: 0, // Disable built-in ofetch retry; $api handles retries with token refresh
  async onRequest({ options }) {
    options.baseURL = (useRuntimeConfig().public.apiBaseUrl as string) || '/api'

    const supabase = useSupabaseClient()
    const { data: { session } } = await supabase.auth.getSession()
    const accessToken = session?.access_token

    const headers = normalizeHeaders(options)

    headers.Authorization = `Bearer ${accessToken}`

    if (['POST', 'PUT', 'PATCH'].includes((options.method || 'GET').toUpperCase())) {
      if (!headers['Content-Type'])
        headers['Content-Type'] = 'application/json'
    }
  },
})

/**
 * Authenticated API client with automatic 401 recovery.
 * On a 401 response (client-side only), the token is refreshed once and
 * the request is retried. If the refresh fails, the user is redirected to /login.
 */
export const $api: typeof $fetch = (async (url: string, options?: FetchOptions) => {
  try {
    return await (_baseFetch as any)(url, options)
  }
  catch (err: any) {
    // Only attempt 401 recovery on the client
    if (!import.meta.client || err?.response?.status !== 401) {
      throw err
    }

    // One retry: refresh the token and replay the request
    try {
      const supabase = useSupabaseClient()
      const { data: { session }, error } = await supabase.auth.refreshSession()

      if (error || !session) {
        console.warn('[API] Token refresh failed, redirecting to login:', error?.message)

        const authStore = useAuthStore()

        authStore.setUser(null)
        await navigateTo('/login')
        throw err // re-throw original error
      }

      // Replay with the fresh token
      const retryOptions: FetchOptions = { ...options }
      const headers = normalizeHeaders(retryOptions)

      headers.Authorization = `Bearer ${session.access_token}`

      return await (_baseFetch as any)(url, retryOptions)
    }
    catch (retryErr: any) {
      // If the retry itself is a 401 again, redirect to login
      if (retryErr?.response?.status === 401) {
        console.warn('[API] Retry still returned 401, redirecting to login')

        const authStore = useAuthStore()

        authStore.setUser(null)
        await navigateTo('/login')
      }
      throw retryErr
    }
  }
}) as typeof $fetch

// Preserve $fetch.create / .raw etc. by copying static methods
Object.assign($api, _baseFetch)
